import { useEffect, useMemo, useState } from 'react';
import { firestore } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

export interface PartnerRadioForInterviews {
  id: string;
  name?: string;
  logo?: string;
}

export interface PartnerInterview {
  id: string;
  link: string;
  partnerId: string;
  title?: string;
  thumbnailUrl?: string;
  publishedAt?: string | null; // ISO string if available
}

const normalizeYoutubeUrl = (raw: string) => {
  const url = (raw || '').trim();
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('www.youtube.com') || url.startsWith('youtube.com')) return `https://${url}`;
  if (url.startsWith('youtu.be/')) return `https://www.youtube.com/${url.replace('youtu.be/', '')}`;
  return url;
};

const getYouTubeVideoId = (urlOrId: string) => {
  const input = (urlOrId || '').trim();
  if (!input) return '';

  // If someone pasted the video id directly
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;

  // youtu.be/<id>
  const shortMatch = input.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch?.[1]) return shortMatch[1];

  // youtube.com/watch?v=<id>
  const watchMatch = input.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (watchMatch?.[1]) return watchMatch[1];

  // youtube.com/embed/<id>
  const embedMatch = input.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch?.[1]) return embedMatch[1];

  return '';
};

const formatPtDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fetchYouTubeOEmbed = async (videoUrl: string) => {
  const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`;
  const res = await fetch(oembedUrl);
  if (!res.ok) throw new Error(`oEmbed HTTP ${res.status}`);
  return (await res.json()) as {
    title?: string;
    thumbnail_url?: string;
  };
};

const buildCanonicalWatchUrl = (videoId: string) => `https://www.youtube.com/watch?v=${videoId}`;

export const useRadioInterviews = (radioId: string) => {
  const [radio, setRadio] = useState<PartnerRadioForInterviews | null>(null);
  const [interviews, setInterviews] = useState<PartnerInterview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const normalizedRadioId = useMemo(() => (radioId || '').trim(), [radioId]);

  useEffect(() => {
    const fetchRadioAndInterviews = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1) Rádio (pode falhar por regras, mas não deve derrubar a página toda)
        try {
          const radioDoc = await getDoc(doc(firestore, 'parcerias', normalizedRadioId));
          if (radioDoc.exists()) {
            const data = radioDoc.data() as any;
            setRadio({
              id: radioDoc.id,
              name: data?.name,
              logo: data?.logo,
            });
          }
        } catch (err: any) {
          console.warn('Erro ao ler doc de parcerias:', err);
        }

        // 2) Entrevistas
        let interviewsSnapshot;
        try {
          interviewsSnapshot = await getDocs(collection(firestore, 'parcerias-entrevistas'));
        } catch (err: any) {
          const msg = err?.message ? String(err.message) : '';
          if (msg.toLowerCase().includes('missing') || msg.toLowerCase().includes('permission')) {
            setError(
              'Sem permissões para ler `parcerias-entrevistas`. Ajusta as Security Rules no Firebase para permitir leitura pública.'
            );
          } else {
            setError(err?.message || 'Erro ao carregar entrevistas');
          }
          setInterviews([]);
          return;
        }

        const allDocs = interviewsSnapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        }));

        const filtered = allDocs.filter((d: any) => {
          const partnerRef = d?.parcerias ?? d?.parceria;
          if (typeof partnerRef === 'string') {
            if (partnerRef === normalizedRadioId) return true;
            // Ex: "parcerias/<id>" ou "/parcerias/<id>"
            if (partnerRef.includes('/parcerias/') && partnerRef.endsWith(`/${normalizedRadioId}`)) return true;
            if (partnerRef.includes('parcerias/') && partnerRef.endsWith(`/${normalizedRadioId}`)) return true;
          }
          const partnerIdFromRef = partnerRef?.id;
          if (partnerIdFromRef) return partnerIdFromRef === normalizedRadioId;

          const partnerPath = partnerRef?.path;
          if (partnerPath) return partnerPath === `parcerias/${normalizedRadioId}`;

          return false;
        });

        const interviewsBase: PartnerInterview[] = filtered
          .map((d: any) => {
            const linkRaw = d?.link ?? d?.url ?? '';
            const link = normalizeYoutubeUrl(linkRaw);
            const videoId = getYouTubeVideoId(linkRaw);
            const canonicalUrl = videoId ? buildCanonicalWatchUrl(videoId) : link;
            return {
              id: d.id,
              link: canonicalUrl,
              partnerId: normalizedRadioId,
              title: undefined,
              thumbnailUrl: undefined,
              publishedAt: d?.publishedAt ?? d?.date ?? null,
            } as PartnerInterview;
          })
          .filter((x) => Boolean(getYouTubeVideoId(x.link)));

        if (interviewsBase.length === 0) {
          setInterviews([]);
          return;
        }

        // Enriquecer com metadata do YouTube (sem API key), via oEmbed.
        // Também deduplicamos requests por videoId para reduzir carga.
        const videoIdList = Array.from(new Set(interviewsBase.map((x) => getYouTubeVideoId(x.link)).filter(Boolean)));
        const cacheById = new Map<string, { title?: string; thumbnailUrl?: string }>();

        await Promise.all(
          videoIdList.map(async (videoId) => {
            try {
              const o = await fetchYouTubeOEmbed(buildCanonicalWatchUrl(videoId));
              cacheById.set(videoId, {
                title: o?.title,
                thumbnailUrl: o?.thumbnail_url,
              });
            } catch {
              // Mantém cache vazio; a entrevista ficará sem título/thumbnail extras
            }
          })
        );

        const enriched = interviewsBase.map((it) => {
          const videoId = getYouTubeVideoId(it.link);
          const cached = videoId ? cacheById.get(videoId) : undefined;
          return {
            ...it,
            title: cached?.title ?? it.title,
            thumbnailUrl: cached?.thumbnailUrl ?? it.thumbnailUrl,
          };
        });

        // Ordena: por data se existir, senão por título/id
        enriched.sort((a, b) => {
          const da = a.publishedAt ? new Date(a.publishedAt).getTime() : null;
          const db = b.publishedAt ? new Date(b.publishedAt).getTime() : null;
          if (da && db) return db - da; // mais recentes primeiro
          return (b.title || b.id).localeCompare(a.title || a.id);
        });

        setInterviews(enriched);
      } catch (err: any) {
        setError(err?.message || 'Erro ao carregar entrevistas');
      } finally {
        setLoading(false);
      }
    };

    if (normalizedRadioId) {
      fetchRadioAndInterviews();
    } else {
      setLoading(false);
    }
  }, [normalizedRadioId]);

  return {
    radio,
    interviews,
    loading,
    error,
    formatPtDate,
  };
};

